import React, { useState, useEffect, useCallback } from 'react';
import { FiCheckCircle, FiXCircle, FiAlertCircle, FiInfo, FiX } from 'react-icons/fi';

const NotificationToast = ({ notifications, removeNotification }) => {
  return (
    <div className="fixed top-1/4 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 space-y-4 w-full max-w-xl px-4">
      {notifications.map((notification) => (
        <NotificationItem
          key={notification.id}
          notification={notification}
          onRemove={() => removeNotification(notification.id)}
        />
      ))}
    </div>
  );
};

const NotificationItem = ({ notification, onRemove }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  const handleExit = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => {
      onRemove();
    }, 300); // Match exit animation duration
  }, [onRemove]);

  useEffect(() => {
    // Trigger enter animation
    setTimeout(() => setIsVisible(true), 50);

    // Auto-remove after duration
    const timer = setTimeout(() => {
      handleExit();
    }, notification.duration || 5000);

    return () => clearTimeout(timer);
  }, [notification.duration, handleExit]);

  const getIcon = () => {
    switch (notification.type) {
      case 'success':
        return <FiCheckCircle className="h-5 w-5 text-green-400" />;
      case 'error':
        return <FiXCircle className="h-5 w-5 text-red-400" />;
      case 'warning':
        return <FiAlertCircle className="h-5 w-5 text-yellow-400" />;
      case 'info':
      default:
        return <FiInfo className="h-5 w-5 text-blue-400" />;
    }
  };

  const getBackgroundColor = () => {
    switch (notification.type) {
      case 'success':
        return 'bg-green-50 border-green-200';
      case 'error':
        return 'bg-red-50 border-red-200';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200';
      case 'info':
      default:
        return 'bg-blue-50 border-blue-200';
    }
  };

  return (
    <div
      className={`
        w-full shadow-xl rounded-lg pointer-events-auto border
        ${getBackgroundColor()}
        transform transition-all duration-300 ease-in-out
        ${isVisible && !isExiting ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0'}
        ${isExiting ? '-translate-y-4 opacity-0' : ''}
      `}
    >
      <div className="p-6">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            {getIcon()}
          </div>
          <div className="ml-4 w-0 flex-1">
            {notification.title && (
              <p className="text-base font-medium text-gray-900">
                {notification.title}
              </p>
            )}
            <p className={`text-sm text-gray-700 ${notification.title ? 'mt-1' : ''}`}>
              {notification.message}
            </p>
          </div>
          <div className="ml-4 flex-shrink-0 flex">
            <button
              className="inline-flex text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 rounded-md p-1"
              onClick={handleExit}
            >
              <span className="sr-only">Close</span>
              <FiX className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationToast; 